import type { ModerationActionType, ModerationRule, Restriction, StaffAuditAction, StandingAction, StandingLevel } from '@spawnwire/shared';

export const RULE_LABEL: Record<ModerationRule, string> = {
  spam: 'Spam',
  harassment: 'Harassment or bullying',
  hate: 'Hateful conduct',
  violence: 'Violence or threats',
  self_harm: 'Self-harm',
  sexual: 'Sexual content',
  scam: 'Scams or phishing',
  cheating: 'Cheating or exploits',
  impersonation: 'Impersonation',
  server_abuse: 'Abusing game server hosting',
  other: 'Other',
};

export const RESTRICTION_LABEL: Record<Restriction, string> = {
  messaging: 'Messaging',
  hosting: 'Game server hosting',
};

export const RESTRICTION_HINT: Record<Restriction, string> = {
  messaging: "Can't send or edit messages, react, upload files, start group chats or send friend requests.",
  hosting: "Can't create, start or change game servers. Running servers are stopped.",
};

export const TYPE_LABEL: Record<ModerationActionType, string> = { warning: 'Warning', ban: 'Ban', restriction: 'Restriction' };

export const LEVEL_INFO: Record<StandingLevel, { label: string; title: string; body: string; tone: string; bar: string }> = {
  good: {
    label: 'All good',
    title: 'Your account is in good standing',
    body: 'Thanks for following the Spawnwire rules. Nothing to see here.',
    tone: 'text-success',
    bar: 'bg-success',
  },
  limited: {
    label: 'Limited',
    title: 'Your account is limited',
    body: 'You have an active warning or restriction. It clears on its own when it expires.',
    tone: 'text-warning',
    bar: 'bg-warning',
  },
  at_risk: {
    label: 'At risk',
    title: 'Your account is at risk',
    body: 'You have several recent warnings or a recent ban. Breaking the rules again is likely to get your account banned.',
    tone: 'text-[#f97316]',
    bar: 'bg-[#f97316]',
  },
  banned: {
    label: 'Banned',
    title: 'Your account is banned',
    body: 'You can still read why and appeal.',
    tone: 'text-danger',
    bar: 'bg-danger',
  },
};

export const LEVELS: StandingLevel[] = ['good', 'limited', 'at_risk', 'banned'];

export const AUDIT_LABEL: Record<StaffAuditAction, string> = {
  user_warn: 'warned',
  user_ban: 'banned',
  user_restrict: 'restricted',
  action_revoke: 'lifted an action on',
  staff_role: 'changed the staff role of',
  report_review: 'reviewed a report',
  appeal_review: 'reviewed an appeal from',
  server_suspend: 'suspended the server',
  server_unsuspend: 'unsuspended the server',
  server_stop: 'stopped the server',
  server_delete: 'deleted the server',
  space_delete: 'removed the space',
  discovery_review: 'reviewed the Discover listing for',
  ticket_reply: 'replied to support ticket',
  ticket_status: 'changed support ticket',
};

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** "3 days", "5 hours", "permanent" for a ban length in hours. */
export function formatHours(hours: number | null): string {
  if (hours === null) return 'permanent';
  if (hours % 24 === 0) return `${hours / 24} day${hours === 24 ? '' : 's'}`;
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}

/** Time left until an ISO date, as "2 days 4 hours" / "35 minutes". */
export function timeLeft(iso: string, now = Date.now()): string {
  const ms = Math.max(0, Date.parse(iso) - now);
  const mins = Math.ceil(ms / 60_000);
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  if (days > 0) return `${days} day${days === 1 ? '' : 's'}${hours ? ` ${hours} hour${hours === 1 ? '' : 's'}` : ''}`;
  if (hours > 0) return `${hours} hour${hours === 1 ? '' : 's'}${mins % 60 ? ` ${mins % 60} min` : ''}`;
  return `${mins} minute${mins === 1 ? '' : 's'}`;
}

/** One line on whether an action is still in force, for the person or for staff. */
export function statusLine(a: StandingAction): string {
  if (a.revokedAt) return `Lifted ${formatDate(a.revokedAt)}`;
  if (a.type === 'warning') return a.active ? `Counts toward your standing until ${formatDate(a.expiresAt!)}` : 'No longer counts toward your standing';
  if (!a.active) return `Ended ${formatDate(a.expiresAt!)}`;
  return a.expiresAt ? `Ends ${formatDate(a.expiresAt)} (in ${timeLeft(a.expiresAt)})` : 'Permanent';
}

/** What an action is, e.g. "Warning", "Restriction: Messaging". */
export function actionTitle(a: Pick<StandingAction, 'type' | 'restriction'>): string {
  return a.type === 'restriction' && a.restriction ? `Restriction: ${RESTRICTION_LABEL[a.restriction]}` : TYPE_LABEL[a.type];
}
